/**
 * Historical Session Browser Types
 *
 * Task 2561: Session Browser — Master-Detail archetype (Browser/Explorer flavor).
 * Aligned with backend agent session schemas.
 */

// ── Session Status & Source ─────────────────────────────────────────────────

/**
 * Session status surfaced on the sessions list / inspector.
 *
 * - `running`   — Tracked live by the agents backend in-memory Fleet Pulse
 *                 tracker (genuine in-flight execution).
 * - `in-review` — Paused at a tool-approval gate awaiting operator review.
 *                 NOTE: text-review pendings (responses queued for the
 *                 "Responses" tab) do NOT use this status — those sessions
 *                 are reported as `success` since the agent has finished.
 * - `success`   — Terminal: completed normally, or paused-then-resolved
 *                 (approved / rejected / suppressed tool approvals).
 * - `error`     — Terminal: state["error"] truthy.
 * - `cancelled` — Terminal: state["cancelled"] truthy.
 */
export type SessionStatus =
  | 'success'
  | 'error'
  | 'cancelled'
  | 'in-review'
  | 'running'
export type SessionSource = 'playground' | 'production'

// ── Core Session List Item ──────────────────────────────────────────────────

/**
 * Compact session record for the list pane.
 * Produced by GET /v1/agents/sessions (cross-agent) or GET /v1/agents/{id}/sessions.
 * Task 2605.
 */
export interface SessionListItem {
  sessionId: string
  agentId: string
  agentName: string
  userId?: string
  userEmail?: string
  status: SessionStatus
  source: SessionSource
  startedAt: string // ISO 8601 — when the session was first created
  /**
   * ISO 8601 timestamp of the session's last activity.  Tracks
   * ``StorageSession.update_time`` server-side and is the field the list
   * endpoint orders by, so a long-running session that started yesterday
   * but had a tool turn moments ago floats to the top of the list (and
   * lands in the "Today" bucket).  Always populated; falls back to
   * ``startedAt`` when the session has had no activity since creation.
   *
   * Distinct from ``endedAt``, which the backend leaves null when the
   * session has had no activity since creation — i.e. ``endedAt`` answers
   * "has this session finished?" while ``updatedAt`` answers "when was it
   * last touched?".
   */
  updatedAt: string // ISO 8601 — last activity (sort key)
  endedAt?: string // ISO 8601
  /**
   * Real execution time in milliseconds — the cumulative sum of every
   * invocation's wall-clock latency, written by the runner via
   * ``state["duration_ms"]``.  Always represents compute actually
   * performed, never calendar elapsed.
   *
   * ``undefined`` for sessions where the runner has not (yet) written
   * this value (legacy rows, sessions still in-flight, sessions whose
   * runner crashed before the bookkeeping write).  Surfaces as "—" in
   * the table — strictly preferable to a misleading number.
   *
   * Distinct from ``wallSpanMs``: this counts seconds-of-compute,
   * ``wallSpanMs`` counts seconds-of-lifetime.
   */
  durationMs?: number
  /**
   * Calendar span in milliseconds: ``updatedAt - startedAt``.  Always
   * populated when both timestamps exist.  Represents how long the
   * session has been alive, NOT how much compute it consumed.
   *
   * Useful for spotting long-running sessions in the inspector
   * ("this session has been alive for 6h, with 30s of compute") but
   * deliberately not displayed as "Duration" in the list — that would
   * imply cost where there is none.
   */
  wallSpanMs?: number
  /**
   * Count of ADK ``Event`` rows persisted in the session DB for this session.
   *
   * NOTE: despite the field name this is **not** the number of conversation
   * turns that the transcript view renders.  The session DB stores one
   * ``Event`` per ADK runner emit (model output, tool call, tool response,
   * etc.); the transcript renderer
   * (``services/agents/backend/internal/handlers/http/audit_api.py::_events_to_transcript_turns``)
   * collapses those into a smaller set of user/assistant/tool/a2a_call
   * turns.  Surfaces consuming this field should label it as "events", not
   * "turns", to avoid the table-vs-transcript count mismatch operators
   * have reported.
   *
   * Provenance: ``services/agents/backend/internal/handlers/http/api.py:4799``
   * — ``state.get("turn_count") or int(event_count)``.  The runner never
   * writes ``state["turn_count"]`` today, so the value always falls back
   * to the SQL row's ``event_count``.
   */
  turnCount: number
  totalCostUsd?: number
  /**
   * Session-wide token totals lifted from the Firestore ``cost_tracker``
   * collection.  Absent on sessions that have no cost-tracker doc yet
   * (very new sessions, or sessions before the cost tracker was wired up).
   *
   * ``totalCachedTokens`` is the portion of input tokens served from the
   * Gemini context cache — useful for spotting sessions that benefit (or
   * don't) from the per-agent context-cache configuration.
   */
  totalInputTokens?: number
  totalOutputTokens?: number
  totalCachedTokens?: number
  /**
   * Context-cache storage cost paid by this session (per-token-hour
   * SKU; distinct from `totalCostUsd` which is invocation cost only).
   * Sessions that create or refresh a Gemini context cache pay storage
   * over the cache's TTL even if they never see a cache hit themselves.
   */
  cacheStorageCostUsd?: number
  /**
   * Per-model cost slice for sessions that fan out across multiple models
   * (failover or A2A sub-agent delegation).  Keyed by model id.  Surfaced
   * on the list response so the inspector can render the breakdown
   * without a second round-trip.  Mirrors the agent-cost dashboard's
   * `PerModelBreakdownEntry` shape.
   */
  perModelBreakdown?: Record<
    string,
    {
      inputTokens: number
      outputTokens: number
      cachedTokens: number
      costUsd: number
      cacheStorageCostUsd: number
      invocationCount: number
    }
  >
  tags?: string[]
  /**
   * Set on A2A subagent sessions to the ID of the parent session that
   * triggered this invocation.  Absent on top-level sessions.
   * Task 3115.
   */
  parentSessionId?: string
}

/**
 * Session list API response.
 */
export interface SessionListResponse {
  sessions: SessionListItem[]
  cursor?: string
  hasMore: boolean
}

// ── Transcript Types ────────────────────────────────────────────────────────

export type TurnRole = 'user' | 'assistant' | 'tool' | 'a2a_call'

/**
 * An inline image attached to a transcript turn.
 *
 * Sourced from ADK ``inline_data`` parts (``{ mime_type, data }``) where the
 * mime type is an ``image/*``.  ``data`` is the raw base64 payload (no
 * ``data:`` URI prefix); renderers compose the data URI as
 * ``data:${mimeType};base64,${data}``.
 */
export interface TranscriptImage {
  /** e.g. ``image/png``, ``image/jpeg``. */
  mimeType: string
  /** Base64-encoded image bytes, without the ``data:`` prefix. */
  data: string
}

/**
 * A single conversation turn in a session transcript.
 *
 * Task 3113: ``a2a_call`` turns are emitted when the parent agent invokes a
 * subagent via A2A.  Extra fields (``targetAgent``, ``a2aSessionId``,
 * ``depth``) are present only on ``a2a_call`` turns.
 *
 * Agent-handoff marker (Stream): ``author`` carries the ADK ``Event.author``
 * of the event this turn was emitted from — ``"user"`` for user turns, or the
 * name of the agent (e.g. ``"support_manager"``, ``"email_writer"``) for
 * assistant/tool turns.  For tool turns specifically, ``author`` is the
 * originating model event's author (the agent that called the tool), not the
 * tool-runtime author on the function_response event.  ``transferredToAgent``
 * is set on the tool turn for a ``transfer_to_agent`` call and names the
 * agent control was handed off to.  Both fields are optional so old
 * `/history` payloads (without the backend serialisation change) continue to
 * render — they simply won't show handoff markers.
 */
export interface TranscriptTurnItem {
  turnIndex: number
  role: TurnRole
  toolName?: string
  content: string | Record<string, unknown>
  timestamp: string // ISO 8601
  durationMs?: number
  costUsd?: number
  // a2a_call-specific fields (Task 3113)
  targetAgent?: string
  a2aSessionId?: string
  depth?: number
  // Agent-handoff marker fields
  author?: string
  transferredToAgent?: string
  /**
   * Extended-thinking / reasoning text emitted by the model on this turn.
   *
   * ADK surfaces "thought" parts (``{ thought: true, text: '...' }``) on
   * model events alongside the normal output text — they represent the
   * model's internal chain-of-thought reasoning before producing the
   * response.  When present this field carries the joined thought text so
   * transcript renderers can display it as a clearly de-emphasised block
   * above the response (lighter grey, not bold) to differentiate it from
   * the assistant's actual output.
   *
   * Optional so legacy turn payloads without thinking continue to render
   * unchanged.  Only populated on assistant turns by the history adapter.
   */
  thinkingText?: string
  /**
   * Inline images attached to this turn (e.g. a screenshot handed to the
   * agent on a user turn, or an image emitted by the model).  Sourced from
   * ADK ``inline_data`` parts.  Optional and omitted entirely when the turn
   * carries no images, so legacy payloads and exact-match tests are
   * unaffected.
   */
  images?: TranscriptImage[]
}

/**
 * Paginated chunk of transcript turns.
 * Returned by GET /v1/sessions/{session_id}/trace?after_turn=N&limit=50
 */
export interface TranscriptChunk {
  turns: TranscriptTurnItem[]
  nextAfterTurn?: number // undefined means no more turns
}

// ── Artifacts ───────────────────────────────────────────────────────────────

export interface SessionArtifact {
  id: string
  name: string
  type: string
  content?: string | Record<string, unknown>
  createdAt: string
}

export interface SessionArtifactsResponse {
  artifacts: SessionArtifact[]
}

// ── Filter & Sort Types ─────────────────────────────────────────────────────

export interface SessionFilters {
  q?: string
  agent_id?: string[]
  status?: SessionStatus[]
  source?: SessionSource[]
  user_id?: string
  from?: string // ISO date
  to?: string // ISO date
  min_duration_s?: number
  min_cost_usd?: number
  /**
   * When true, include A2A subagent sessions in the list.
   * Defaults to false (subagent sessions are hidden by default).
   * Task 3115.
   */
  include_subagents?: boolean
}

export interface SessionSortState {
  column: 'endedAt' | 'startedAt' | 'durationMs' | 'totalCostUsd' | 'turnCount' | null
  direction: 'asc' | 'desc' | null
}

// ── Time Bucket Grouping ────────────────────────────────────────────────────

export type TimeBucket = 'today' | 'yesterday' | 'this-week' | 'this-month' | 'older'

export interface TimeBucketGroup {
  bucket: TimeBucket
  label: string
  sessions: SessionListItem[]
}

/**
 * Compute the time bucket for an ISO date string.
 *
 * Callers should pass the session's ``updatedAt`` (last activity time)
 * rather than ``startedAt`` so a long-running session that started days
 * ago but had a tool turn moments ago lands in the "Today" bucket — this
 * matches the server-side ORDER BY in ``_list_sessions_enriched`` so the
 * grouping and the row order stay aligned.
 */
export function getTimeBucket(isoDate: string): TimeBucket {
  const date = new Date(isoDate)
  const now = new Date()

  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)

  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)

  const startOfWeek = new Date(startOfToday)
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)

  if (date >= startOfToday) return 'today'
  if (date >= startOfYesterday) return 'yesterday'
  if (date >= startOfWeek) return 'this-week'
  if (date >= startOfMonth) return 'this-month'
  return 'older'
}

const BUCKET_LABELS: Record<TimeBucket, string> = {
  'today': 'Today',
  'yesterday': 'Yesterday',
  'this-week': 'This week',
  'this-month': 'This month',
  'older': 'Older',
}

const BUCKET_ORDER: TimeBucket[] = ['today', 'yesterday', 'this-week', 'this-month', 'older']

/**
 * Group a flat session list into time buckets (recency-first).
 */
export function groupSessionsByBucket(sessions: SessionListItem[]): TimeBucketGroup[] {
  const bucketMap = new Map<TimeBucket, SessionListItem[]>()

  for (const session of sessions) {
    // Bucket by last-activity time (``updatedAt``) — matches the server-side
    // ORDER BY so the bucket headers reflect when the session was last
    // touched, not when it was first created.  Falls back to ``startedAt``
    // for legacy rows that pre-date the ``updatedAt`` field.
    const dateStr = session.updatedAt ?? session.startedAt
    const bucket = getTimeBucket(dateStr)
    if (!bucketMap.has(bucket)) bucketMap.set(bucket, [])
    bucketMap.get(bucket)!.push(session)
  }

  return BUCKET_ORDER
    .filter((b) => bucketMap.has(b))
    .map((b) => ({
      bucket: b,
      label: BUCKET_LABELS[b],
      sessions: bucketMap.get(b)!,
    }))
}

// ── Transcript Cache ────────────────────────────────────────────────────────

export type TranscriptStatus = 'idle' | 'loading' | 'loaded' | 'error' | 'complete'

export interface TranscriptCache {
  turns: TranscriptTurnItem[]
  nextAfterTurn?: number
  status: TranscriptStatus
  error?: string
}

// ── Inspector Tab ───────────────────────────────────────────────────────────

export type SessionInspectorTab = 'transcript' | 'artifacts' | 'trace'

// ── In-Transcript Search ────────────────────────────────────────────────────

export interface TranscriptSearchState {
  query: string
  matches: number[] // turnIndex values that match
  currentMatchIndex: number
}

// ── Filter Presets ──────────────────────────────────────────────────────────

export interface SessionFilterPreset {
  id: string
  label: string
  filters: SessionFilters
  isDefault?: boolean
}

export const DEFAULT_SESSION_PRESETS: SessionFilterPreset[] = [
  {
    id: 'all',
    label: 'All',
    filters: {},
    isDefault: true,
  },
  {
    id: 'errors',
    label: 'Errors (24h)',
    filters: { status: ['error'] },
  },
  {
    id: 'playground',
    label: 'Playground',
    filters: { source: ['playground'] },
  },
]
